import React, { useState, useEffect } from "react";
import { Card, Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import bg1 from "../assets/img/bg1.jpg";
import { useAuth } from "../hooks/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import axiosInstance from "../api/axios";

const VerifyEmail = () => {
  const [userEmail, setUserEmail] = useState('');
  const [status, setStatus] = useState('idle'); // 'idle', 'loading', 'success', 'error'
  const [message, setMessage] = useState('');

  const handleChange = (e) => {
    setUserEmail(e.target.value);
    if (status !== 'idle') setStatus('idle');
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    
    if (!userEmail) {
      setStatus('error');
      setMessage('Please enter your email address');
      return;
    }

    setStatus('loading');
    
    try {
      const response = await axiosInstance.post(`admin-user-management/verify-email?email=${userEmail}`);
      console.log("Response :", response);
      
      setStatus('success');
      setMessage('📧 Verification email sent! Check your inbox and spam folder.');
      
      // Create floating email animation
      createFloatingEmails();
      
    } catch (error) {
      console.log("Error :", error);
      setStatus('error');
      setMessage(error.response?.data?.message || 'Something went wrong. Please try again.');
    }
  };

  const createFloatingEmails = () => {
    const container = document.querySelector('.email-animation-container');
    if (!container) return;

    for (let i = 0; i < 5; i++) {
      const email = document.createElement('div');
      email.innerHTML = '✉️';
      email.style.position = 'absolute';
      email.style.left = `${Math.random() * 100}%`;
      email.style.bottom = '0';
      email.style.fontSize = '24px';
      email.style.opacity = '0';
      email.style.zIndex = '1';
      
      container.appendChild(email);

      // Animate email floating up
      setTimeout(() => {
        email.style.transition = 'all 2s ease-out';
        email.style.opacity = '1';
        email.style.transform = `translateY(-${200 + Math.random() * 100}px) rotate(${Math.random() * 360}deg)`;
        email.style.opacity = '0';
      }, i * 200);

      // Remove element after animation
      setTimeout(() => {
        if (email.parentNode) {
          email.parentNode.removeChild(email);
        }
      }, 2500);
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'success':
        return '🎉';
      case 'error':
        return '❌';
      case 'loading':
        return '⏳';
      default:
        return '🔒';
    }
  };

  return (
    <div className="page-sign d-block py-0">
      <Row className="g-0">
        <Col md="7" lg="5" xl="4" className="col-wrapper">
          <Card className="card-sign glassmorphism-card">
            <div className="email-animation-container" style={{ position: 'relative', overflow: 'hidden' }}>
              <Card.Header className="text-center">
                <Link to="/" className="header-logo mb-4 d-block">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 200 }}
                    className="logo-icon"
                  >
                    {/* 🚀 */}
                  </motion.div>
                  xpanse admin
                </Link>
                
                <motion.div
                  initial={{ y: -20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="status-icon" style={{ fontSize: '3rem', marginBottom: '1rem' }}>
                    {getStatusIcon()}
                  </div>
                  <Card.Title className="text-gradient">Verify Your Email</Card.Title>
                  <Card.Text className="text-muted">
                    We'll send you a magic link to verify your account
                  </Card.Text>
                </motion.div>
              </Card.Header>

              <Card.Body>
                <AnimatePresence mode="wait">
                  {status === 'success' ? (
                    <motion.div
                      key="success"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      className="text-center"
                    >
                      <div className="success-checkmark mb-3">✅</div>
                      <h5 className="text-success">Check Your Email!</h5>
                      <p className="text-muted">{message}</p>
                      <Button 
                        variant="outline-primary" 
                        onClick={() => {
                          setStatus('idle');
                          setUserEmail('');
                        }}
                      >
                        Send Another Email
                      </Button>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="form"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                    >
                      <Form onSubmit={handleVerify}>
                        <div className="mb-4">
                          <Form.Label className="fw-bold">Email Address</Form.Label>
                          <motion.div whileFocus={{ scale: 1.02 }}>
                            <Form.Control
                              type="email"
                              placeholder="Enter your email address"
                              value={userEmail}
                              onChange={handleChange}
                              disabled={status === 'loading'}
                              className="modern-input"
                            />
                          </motion.div>
                        </div>

                        <AnimatePresence>
                          {(status === 'error' || status === 'success') && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className={`alert alert-${status === 'error' ? 'danger' : 'success'} mb-3`}
                            >
                              {message}
                            </motion.div>
                          )}
                        </AnimatePresence>

                        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                          <Button
                            type="submit"
                            variant="primary"
                            disabled={status === 'loading' || !userEmail}
                            className="w-100 modern-button"
                            size="lg"
                          >
                            {status === 'loading' ? (
                              <>
                                <Spinner
                                  as="span"
                                  animation="border"
                                  size="sm"
                                  role="status"
                                  aria-hidden="true"
                                  className="me-2"
                                />
                                Sending Magic Link...
                              </>
                            ) : (
                              <>
                                ✨ Send Verification Email
                              </>
                            )}
                          </Button>
                        </motion.div>
                      </Form>
                    </motion.div>
                  )}
                </AnimatePresence>

                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="text-center mt-4"
                >
                  <small className="text-muted">
                    Remember your password? <Link to="/" className="text-decoration-none">Sign In</Link>
                  </small>
                </motion.div>
              </Card.Body>
            </div>
          </Card>
        </Col>
        
        <Col className="d-none d-md-block position-relative">
          <div 
            className="auth-img"
            style={{
              backgroundImage: `url(${bg1})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              height: '100vh',
              position: 'relative'
            }}
          >
            <div className="overlay-gradient"></div>
          </div>
        </Col>
      </Row>

      <style>{`
        .glassmorphism-card {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          border: none;
          border-radius: 20px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
        }
        
        .text-gradient {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .modern-input {
          border: 2px solid #e9ecef;
          border-radius: 12px;
          padding: 12px 16px;
          transition: all 0.3s ease;
        }
        
        .modern-input:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
        }
        
        .modern-button {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          border-radius: 12px;
          padding: 12px;
          font-weight: 600;
          transition: all 0.3s ease;
        }
        
        .modern-button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
        }
        
        .modern-button:disabled {
          background: #6c757d;
        }
        
        .overlay-gradient {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(102, 126, 234, 0.8) 0%, rgba(118, 75, 162, 0.8) 100%);
        }
        
        .success-checkmark {
          font-size: 4rem;
          animation: bounce 0.6s ease-in-out;
        }
        
        @keyframes bounce {
          0%, 20%, 60%, 100% { transform: translateY(0); }
          40% { transform: translateY(-10px); }
          80% { transform: translateY(-5px); }
        }
        
        .logo-icon {
          display: inline-block;
          font-size: 2rem;
          margin-right: 0.5rem;
        }
      `}</style>
    </div>
  );
};

export default VerifyEmail;